import React, { useContext, useEffect, useRef, useState } from 'react';
import { copyToClipboard } from '../../util/clipboard';
import { Helmet } from 'react-helmet-async';
import { useSelector, useDispatch } from 'react-redux';
import { Button, NamedLink, Page, BlogSkeleton } from '../../components';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { Container } from '../../components/Layout/Layout';
import { LanguageContext } from '../../context/LanguageContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useConfiguration } from '../../context/configurationContext';
import { createSlug } from '../../util/urlHelpers';
import { incrementBlogViewsAndClicks, ensureStoreBlogSlug } from './StoreBlogContentPage.duck';
import { createStoreBlogPath, getStoreBlogSlug } from '../../util/storeBlogs';
import AboutAuthor from './AboutAuthor/AboutAuthor';

import css from './StoreBlogContentPage.module.css';
import { createResourceLocatorString } from '../../util/routes';
import {
  buildCloudinaryHeroImageSet,
  generateCitySlug,
  generateStoreSlug,
  parseCloudinaryDimensions,
} from '../../util/seo';

function htmlSafe(html) {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function hasWords(value) {
  if (!value) return false;
  const text = String(value).replace(/<[^>]*>/g, ' ').trim();
  return text.split(/\s+/).filter(Boolean).length > 0;
}

function getLocalizedBlog(rawBlog = {}, currentLanguage = 'en') {
  const localized = rawBlog?.translations?.[currentLanguage];

  if (!localized) {
    return rawBlog;
  }

  // Clone blog and replace top-level fields
  const { translations, ...baseBlog } = rawBlog;

  return {
    ...baseBlog,
    title: localized.title,
    label: localized.label,
    content: {
      ...rawBlog.content,
      ...localized.content,
    },
  };
}

function StoreBlogContentPage() {
  const {
    blog: rawBlog,
    blogFetched,
    blogLoading,
    blogError,
    user: rawUser,
    userFetched,
    userError,
  } = useSelector(state => state.StoreBlogContentPage || {});

  const { locale } = useContext(LanguageContext);
  const blog = getLocalizedBlog(rawBlog, locale);
  const user = rawUser;

  const { currentUser } = useSelector(state => state.user || {});
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const dispatch = useDispatch();
  const { blogId, userId, slug } = useParams();
  const lastIncrementedBlogIdRef = useRef(null);
  const lastSlugEnsuredBlogIdRef = useRef(null);
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    setCanShare(true);
  }, []);

  const intl = useIntl();

  const resolvedBlogId = blog?._id || blog?.id;
  const resolvedUserId = user?.id?.uuid || user?.id;
  const resolvedBlogSlug = getStoreBlogSlug(blog);
  const hasBlogParam = !!(blogId || slug);
  const blogMatchesRoute = blogId
    ? !!resolvedBlogId && String(resolvedBlogId) === String(blogId)
    : slug
    ? !!resolvedBlogSlug && resolvedBlogSlug === slug
    : !!blog;
  const userMatchesRoute =
    !userId || (!!resolvedUserId && String(resolvedUserId) === String(userId));

  // Increment views/clicks on client only (avoid doing this in SSR)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!resolvedBlogId || blogLoading || blogError || !blog || !blogMatchesRoute) {
      return;
    }
    if (lastIncrementedBlogIdRef.current === resolvedBlogId) {
      return;
    }
    lastIncrementedBlogIdRef.current = resolvedBlogId;
    dispatch(incrementBlogViewsAndClicks(resolvedBlogId, true));
  }, [resolvedBlogId, blogLoading, blogError, blog, blogMatchesRoute, dispatch]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !blogId ||
      !blog ||
      blogLoading ||
      blogError ||
      !blogMatchesRoute ||
      getStoreBlogSlug(blog)
    ) {
      return;
    }

    if (lastSlugEnsuredBlogIdRef.current === resolvedBlogId) {
      return;
    }

    lastSlugEnsuredBlogIdRef.current = resolvedBlogId;
    dispatch(ensureStoreBlogSlug(blog));
  }, [blogId, blog, blogLoading, blogError, blogMatchesRoute, resolvedBlogId, dispatch]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !blog ||
      !resolvedUserId ||
      !userMatchesRoute ||
      !blogMatchesRoute
    ) {
      return;
    }

    const canonicalPath = createStoreBlogPath(
      blog,
      resolvedUserId,
      routeConfiguration,
      { lang: locale }
    );

    if (canonicalPath && history.location.pathname !== canonicalPath) {
      history.replace(canonicalPath);
    }
  }, [
    blog,
    resolvedUserId,
    userMatchesRoute,
    blogMatchesRoute,
    routeConfiguration,
    locale,
    history,
  ]);

  // Loading and error states (user loads in parallel — do not block article body)
  if (blogLoading || (hasBlogParam && !blogFetched && !blogError)) {
    return (
      <Page
        title={intl.formatMessage({
          id: 'StoreBlogContentPage.loadingTitle',
          defaultMessage: 'Loading...',
        })}
      >
        <TopbarContainer />
        <Container>
          <BlogSkeleton />
        </Container>
        <FooterContainer />
      </Page>
    );
  }
  
  if (blogError) {
    return (
      <Page
        title={intl.formatMessage({
          id: 'StoreBlogContentPage.errorTitle',
          defaultMessage: 'Error',
        })}
      >
        <TopbarContainer />
        <div className={css.error}>
          {blogError?.message ||
            intl.formatMessage({
              id: 'StoreBlogContentPage.errorMessage',
              defaultMessage: 'Something went wrong.',
            })}
        </div>
        <FooterContainer />
      </Page>
    );
  }

  // Only show "no blog found" after loading is complete
  if (!blog || !blogMatchesRoute || (blog?.status === 'draft' && currentUser?.id?.uuid !== blog?.userId)) {
    return (
      <Page
        title={intl.formatMessage({
          id: 'StoreBlogContentPage.noBlogTitle',
          defaultMessage: 'No Blog',
        })}
      >
        <TopbarContainer />
        <div className={css.error}>
          {intl.formatMessage({
            id: 'StoreBlogContentPage.noBlogMessage',
            defaultMessage: 'No blog found.',
          })}
        </div>
        <FooterContainer />
      </Page>
    );
  };

  // Map API blog data to UI props
  const { title, label, image, content = {}, createdAt } = blog;
  const updatedAt = blog?.updatedAt || null;
  const {
    excerpt,
    time,
    readingTime,
    metaTags,
    body = {},
    conclusion,
  } = content;
  // Compose sections from body keys section1 ... sectionN
  const intro = body.introduction;
  const sections = Object.keys(body)
    .filter(k => k.startsWith('section'))
    .map(k => body[k]);

  // Use image.imageUrl if present
  const imageUrl   = image?.imageUrl || image?.secure_url;
  const { hero800, hero1600, preloadUrl } = buildCloudinaryHeroImageSet(imageUrl);
  const heroSrc = hero800 || imageUrl;
  const heroSrcSet =
    hero800 && hero1600 && hero800 !== imageUrl
      ? `${hero800} 800w, ${hero1600} 1600w`
      : undefined;
  const { width: imgWidth, height: imgHeight } = parseCloudinaryDimensions(heroSrc || imageUrl);
  const buildSocialImageUrl = (url) => {
    if (!url || typeof url !== 'string' || !url.includes('/upload/')) return url;
    return url.replace('/upload/', '/upload/w_1200,h_630,c_fill,f_auto,q_auto/');
  };
  const socialImageUrl = buildSocialImageUrl(imageUrl);
  const seoTags    = image?.seoTags || null;
  const imageAlt   = seoTags?.altText  || title || '';
  const imageTitle = seoTags?.titleTag || title || '';

  // Format date
  const dateStr = createdAt ? moment(createdAt).format('MMMM Do YYYY') : '';
  // Compose author (user profile) — optional until user fetch completes
  const showUserUi =
    user && userFetched && !userError && userMatchesRoute;
  const author = user || null;
  // Use label as badge if present
  const badge = label ? <span className={css.category}>{label}</span> : null;

  const { id } = user || {};
  const displayName =
    user?.attributes?.profile?.displayName ||
    user?.attributes?.profile?.publicData?.storeName ||
    '';
  const userSlug = !!displayName?.trim() ? createSlug(displayName) : '';
  const authorCitySlug = generateCitySlug(
    user?.attributes?.profile?.publicData?.storeAddress?.city ||
    user?.attributes?.profile?.publicData?.city
  );
  const rootURL = (config?.marketplaceRootURL || '').replace(/\/$/, '');

  const blogPath = createStoreBlogPath(
    blog,
    resolvedUserId || userId,
    routeConfiguration,
    { lang: locale }
  );
  const blogUrl = rootURL && blogPath ? `${rootURL}${blogPath}` : '';

  const authorPath =
    showUserUi && rootURL && id?.uuid
      ? createResourceLocatorString(
        'ProfilePage',
        routeConfiguration,
        {
          ...(authorCitySlug ? { city: authorCitySlug } : {}),
          slug: generateStoreSlug(user?.attributes?.profile?.publicData?.storeName),
          id: id.uuid,
          lang: locale
        },
        {}
      )
      : '';
  const authorUrl = rootURL && authorPath ? `${rootURL}${authorPath}` : '';
  const storeAtId = authorUrl ? `${authorUrl}#local-business` : undefined;

  // Custom share handlers
  const handleSocialShare = platform => {
    if (!canShare || typeof window === 'undefined') return;
    let url = '';
    const shareUrl = window.location.href;
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(title);
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
        break;
      default:
        break;
    }
    window.open(url, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const handleCopyToClipboard = async () => {
    if (!canShare || typeof window === 'undefined') return;
    try {
      const shareUrl = window.location.href;
      await copyToClipboard(shareUrl);
      alert(
        intl.formatMessage({
          id: 'StoreBlogContentPage.copySuccess',
          defaultMessage: 'Link copied! You can now share it in your story, post, or bio.',
        })
      );
    } catch (err) {
      alert(
        intl.formatMessage({
          id: 'StoreBlogContentPage.copyFail',
          defaultMessage: 'Failed to copy link. Please copy it manually.',
        })
      );
    }
  };

  const socialPlatforms = [
    {
      name: 'Facebook',
      icon: <i className="ri-facebook-box-line"></i>,
      handler: () => handleSocialShare('facebook'),
    },
    {
      name: 'Twitter',
      icon: <i className="ri-twitter-x-line"></i>,
      handler: () => handleSocialShare('twitter'),
    },
    {
      name: 'LinkedIn',
      icon: <i className="ri-linkedin-box-line"></i>,
      handler: () => handleSocialShare('linkedin'),
    },
    {
      name: 'WhatsApp',
      icon: <i className="ri-whatsapp-line"></i>,
      handler: () => handleSocialShare('whatsapp'),
    },
    {
      name: 'Instagram',
      icon: <i className="ri-instagram-line"></i>,
      handler: () => handleCopyToClipboard('Instagram'),
    },
    {
      name: 'TikTok',
      icon: <i className="ri-tiktok-line"></i>,
      handler: () => handleCopyToClipboard('TikTok'),
    },
  ];
  

  // Compose SEO/social props for blog
  const pageDescription = excerpt || '';
  const pageAuthor = showUserUi
    ? author?.attributes?.profile?.displayName ||
      author?.attributes?.profile?.publicData?.storeName ||
      ''
    : '';
  const marketplaceName = config?.marketplaceName || '';
  const marketplaceLogoUrl = `${rootURL}/static/webapp-icon-1200x630.jpg`;
  const publisher = marketplaceName
    ? {
        '@type': 'Organization',
        name:    marketplaceName,
        logo: {
          '@type':    'ImageObject',
          url:         marketplaceLogoUrl,
          width:       1200,
          height:      630,
        },
      }
    : undefined;
  const imagesArr = imageUrl ? [imageUrl] : [];
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: pageDescription,
    url: blogUrl || undefined,
    image: imagesArr.length
      ? imagesArr.map(url => ({
          '@type':    'ImageObject',
          url,
          contentUrl: url,
          ...(seoTags?.altText  ? { description: seoTags.altText  } : {}),
          ...(seoTags?.caption  ? { caption:     seoTags.caption  } : {}),
          ...(imgWidth          ? { width:        imgWidth         } : {}),
          ...(imgHeight         ? { height:       imgHeight        } : {}),
        }))
      : [],
    author: {
      '@type': 'Organization',
      name:    pageAuthor,
      ...(authorUrl   ? { url:   authorUrl   } : {}),
      ...(storeAtId   ? { '@id': storeAtId   } : {}),
    },
    datePublished: createdAt,
    ...(updatedAt ? { dateModified: updatedAt } : {}),
    mainEntityOfPage: blogUrl ? { '@type': 'WebPage', '@id': blogUrl } : undefined,
    ...(publisher ? { publisher } : {}),
    inLanguage: 'en',
    ...(label ? { keywords: label } : {}),
    ...(label ? { articleSection: label } : {}),
  };

  const facebookImages = imagesArr.map(() => ({
    url:    socialImageUrl,
    width:  1200,
    height: 630,
    name:   'facebook',
    alt:    imageAlt,
  }));
  const twitterImages = imagesArr.map(img => {
    const regex = /w_(\d+),h_(\d+)/;
    const match = img.match(regex);
    const width = match ? parseInt(match[1], 10) : null;
    const height = match ? parseInt(match[2], 10) : null;
    return {
      url: img,
      width,
      height,
      name: 'twitter',
      alt: imageAlt,
    };
  });

  return (
    <Page
      title={title}
      description={pageDescription}
      author={pageAuthor}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={schema}
      openGraphType="article"
      scrollingDisabled={false}
      className={css.page}
    >
      {preloadUrl ? (
        <Helmet>
          <link rel="preconnect" href="https://res.cloudinary.com" crossOrigin="" />
          <link rel="preload" as="image" href={preloadUrl} fetchpriority="high" />
        </Helmet>
      ) : null}
      <TopbarContainer />
      <Container className={css.containerWrapper}>
        {showUserUi && (
          <>
            <div className={css.alertBox}>
              <div className={css.iconAndText}>
                <span className={css.icon}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 5.33333V13.9955C14 14.3667 13.7035 14.6667 13.3377 14.6667H2.66227C2.29663 14.6667 2 14.3707 2 14.0055V1.99453C2 1.63687 2.29913 1.33333 2.66814 1.33333H9.99787L14 5.33333ZM12.6667 6H9.33333V2.66666H3.33333V13.3333H12.6667V6ZM5.33333 4.66666H7.33333V6H5.33333V4.66666ZM5.33333 7.33333H10.6667V8.66666H5.33333V7.33333ZM5.33333 10H10.6667V11.3333H5.33333V10Z"
                      fill="#535862"
                    />
                  </svg>
                </span>
                <span className={css.text}>
                  <FormattedMessage
                    id="StoreBlogContentPage.blogFromStore"
                    defaultMessage="This blog is from: {storeName}"
                    values={{ storeName: displayName }}
                  />
                </span>
              </div>
              <NamedLink
                className={css.button}
                name="ProfilePage"
                params={{
                  ...(authorCitySlug ? { city: authorCitySlug } : {}),
                  id: id.uuid,
                  slug: userSlug
                }}
              >
                <FormattedMessage
                  id="StoreBlogContentPage.visitStore"
                  defaultMessage="Visit Store"
                />
              </NamedLink>
            </div>
          </>
        )}
        <div className={css.container}>
          <div className={css.cardContent}>
            {/* Blog context bar (e.g. related store) */}
            <div className={css.header}>
              {badge}
              <h1 className={css.title}>{title}</h1>
            </div>
            {excerpt && <div className={css.excerpt}>{excerpt}</div>}
            <div className={css.meta}>
              <span>
                <i className="ri-time-line"></i> {dateStr}
              </span>

              {readingTime > 0 && <span>
                <i className="ri-book-open-line"></i> {readingTime}{' '}
                <FormattedMessage
                  id="StoreBlogContentPage.minutesRead"
                  defaultMessage="minutes read"
                />
              </span>}
            </div>
            {imageUrl && (
              <div className={css.imageContainer}>
                <img
                  className={css.image}
                  src={heroSrc || imageUrl}
                  srcSet={heroSrcSet}
                  sizes="(min-width: 1024px) 800px, 100vw"
                  alt={imageAlt}
                  title={imageTitle}
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  {...(imgWidth  ? { width:  imgWidth  } : {})}
                  {...(imgHeight ? { height: imgHeight } : {})}
                />
                {seoTags?.caption && (
                  <figcaption className={css.lightboxCaption}>
                    {seoTags.caption}
                  </figcaption>
                )}
              </div>
            )}

            {intro && <div className={css.content}>{htmlSafe(intro)}</div>}
            {sections.map((section, idx) => (
              <div className={css.body} key={idx}>
                <div className={css.sectionTitle}>
                  {htmlSafe(section.heading)}
                </div>
                <div className={css.sectionBody}>
                  {htmlSafe(section.content)}
                </div>
              </div>
            ))}
            {hasWords(conclusion) && (
              <div className={css.conclusionBox}>{htmlSafe(conclusion)}</div>
            )}

            <div className={css.sharepostWrapper}>
              <span className={css.socialText}>
                <FormattedMessage
                  id="StoreBlogContentPage.shareThisBlog"
                  defaultMessage="Share this blog"
                />
              </span>

              <div className={css.socialIconsRow}>
                <Button
                  className={css.socialIcon}
                  type="button"
                  variant={'secondary-outline'}
                  icon={<i className="ri-file-copy-line"></i>}
                  arrow={'left'}
                  style={{
                    gap: 8,
                  }}
                  onClick={handleCopyToClipboard}
                >
                  <FormattedMessage
                    id="StoreBlogContentPage.copyLink"
                    defaultMessage="Copy link"
                  />
                </Button>
                {socialPlatforms.map((platform, index) => (
                  <Button
                    key={platform.name}
                    className={css.socialIcon}
                    aria-label={`Share on ${platform.name}`}
                    onClick={platform.handler}
                    type="button"
                    variant={'secondary-outline'}
                    icon={platform.icon}
                    arrow={'left'}
                  ></Button>
                ))}
              </div>
            </div>
          </div>
        </div>
        {showUserUi && author ? (
          <AboutAuthor
            history={history}
            routeConfiguration={routeConfiguration}
            profile={author}
            verified={true}
            intl={intl}
          />
        ) : null}
      </Container>
      <FooterContainer />
    </Page>
  );
}

export default StoreBlogContentPage;
